// shop-frontend/src/pages/ShopMallPreview.jsx
import React, { useEffect, useMemo, useState } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import { getMyShopMallPage, getPublicShopMallPage, getMyShopProducts } from "../utils/api";
import { COMPONENT_MAP, THEMES, ShopLayout } from "../components/mall-theme/SectionComponents";
import { Loader2 } from "lucide-react";

// Helper to normalize props from Backend (flat) OR Editor (nested props)
function resolveProps(section) {
    if (!section) return {};
    
    // 1. Editor Structure (Priority) - props wrapper
    if (section.props && typeof section.props === 'object') {
        return section.props;
    }

    // 2. Backend Structure (Flat + Content) - merge all fields
    const base = { ...section };
    const content = section.content || {};
    
    return {
        ...base,
        ...content,
        // Explicit field mappings for common section types
        shopName: content.shopName || section.shopName || base.shopName,
        bio: content.bio || section.bio || base.bio,
        avatarUrl: content.avatarUrl || section.avatarUrl || base.avatarUrl,
        coverUrl: content.coverUrl || section.coverUrl || base.coverUrl,
        title: content.title || section.title || base.title,
        subtitle: content.subtitle || section.subtitle || base.subtitle,
        imageUrl: content.imageUrl || section.imageUrl || base.imageUrl,
        buttonText: content.buttonText || section.buttonText || base.buttonText,
        buttonLink: content.buttonLink || section.buttonLink || base.buttonLink,
        productIds: content.productIds || section.productIds || base.productIds,
        links: content.links || section.links || base.links,
        content: content.content || section.content?.value || content.value || section.value,
        align: content.align || section.align || base.align,
    };
}

function extractMallResponse(raw) {
    if (!raw) {
        return { mallPage: { sections: [], themeId: 'midnight', pageBg: '', collections: [] }, shop: {}, products: [] };
    }

    const source = raw || {};
    const mallSource = source.mallPage || source.page || source.data?.mallPage || source.data?.page || source;
    const sections = Array.isArray(mallSource?.sections) ? mallSource.sections : [];
    const collections = Array.isArray(mallSource?.collections) ? mallSource.collections : [];
    const customSidebar = Array.isArray(mallSource?.customSidebar) ? mallSource.customSidebar : [];

    const {
        products: mallProducts,
        shopInfo: _ignoredShopInfo,
        shop: _ignoredShop,
        ...restMall
    } = mallSource || {};

    // ✅ Ensure theme fields are explicitly preserved
    const mallPage = {
        ...restMall,
        sections,
        collections,
        customSidebar,
        themeId: mallSource?.themeId || restMall?.themeId || 'midnight',
        pageBg: mallSource?.pageBg || restMall?.pageBg || ''
    };
    const shop = source.shop || source.shopInfo || source.data?.shop || {};



    const productMap = new Map();
    const collect = (product) => {
        if (!product) return;
        const id = String(product._id || product.id || product.productId || "").trim();
        if (!id || productMap.has(id)) return;
        productMap.set(id, product);
    };

    if (Array.isArray(source.products)) source.products.forEach(collect);
    if (Array.isArray(mallProducts)) mallProducts.forEach(collect);
    sections.forEach((section) => {
        if (Array.isArray(section?.products)) section.products.forEach(collect);
    });

    return { mallPage, shop, products: Array.from(productMap.values()) };
}

/* ─────────────────────────────────────────────────────────────────────────
   flattenVariants
   Expand products that have variants so each variant shows as its own card.
   Products without variants pass through unchanged.
   Each virtual card gets:
     _parentId  - real DB _id of the parent product (for the detail-page link)
     _variantId - the variant's own _id (pre-selects the variant on the detail page)
     _variantIdx - numeric index (fallback)
     title/name  - «Parent name — Color / Size»
     image(s)   - variant images first, then parent images
     price      - variant price if set, otherwise parent price
─────────────────────────────────────────────────────────────────────────── */
function flattenVariants(products) {
  const out = [];
  for (const p of Array.isArray(products) ? products : []) {
    const variants = Array.isArray(p?.variants)
      ? p.variants.filter((v) => Array.isArray(v?.optionValues) && v.optionValues.length > 0)
      : [];

    if (!variants.length) {
      // Simple product — keep as-is but strip empty variants array for clarity
      out.push(p);
      continue;
    }

    // Sort variants so the BEST representative for each colour comes first:
    //   1. Discounted + in-stock  (so the card link lands on the discounted size)
    //   2. Discounted (any stock)
    //   3. In-stock
    //   4. Any
    const variantScore = (v) => {
      const inStock = Number(v?.stock || 0) > 0;
      return inStock ? 1 : 0;
    };
    const sortedVariants = [...variants].sort((a, b) => variantScore(b) - variantScore(a));

    // Deduplicate variants by their display label so e.g. Red/S + Red/M + Red/L
    // all collapse to a single "Red" card (pointing to the first matching variant).
    const seenLabels = new Set();

    // If the shop explicitly chose a grouping option, only that option's value
    // counts toward the card label (so we collapse correctly to one card per value).
    const explicitGrouping = String(p.groupingOption || '').trim().toLowerCase();
    for (let i = 0; i < sortedVariants.length; i++) {
      const v = sortedVariants[i];
      // Show only non-size option values (e.g. Color, Style) — skip Size/Weight/etc.
      const SIZE_LIKE = /^(size|sz|weight|wt)$/i;
      const label = v.optionValues
        .filter((ov) => {
          const n = String(ov?.name || '').trim();
          if (SIZE_LIKE.test(n)) return false;
          if (explicitGrouping) return n.toLowerCase() === explicitGrouping;
          return true;
        })
        .map((ov) => String(ov?.value || '').trim())
        .filter(Boolean)
        .join(' / ');

      // Skip if we already made a card for this display label
      const dedupeKey = label || `__v${i}`;
      if (seenLabels.has(dedupeKey)) continue;
      seenLabels.add(dedupeKey);
      const variantImages = Array.isArray(v.images) && v.images.filter(Boolean).length
        ? v.images.filter(Boolean)
        : [];
      const parentImages = Array.isArray(p.images) ? p.images.filter(Boolean) : [];
      const firstImg = variantImages[0] || parentImages[0] || p.image || p.imageUrl || '';
      const variantPrice =
        v.price !== undefined && v.price !== null && String(v.price).trim() !== ''
          ? v.price
          : p.localPrice ?? p.price;

      const parentName = String(p.title || p.name || 'Product');
      const variantName = label ? `${parentName} — ${label}` : parentName;

      // Resolve the colour (non-size) value for this card so we can scope discounts
      const cardColor = v.optionValues
        .filter((ov) => !SIZE_LIKE.test(String(ov?.name || '').trim()))
        .map((ov) => String(ov?.value || '').toLowerCase())[0] || '';

      out.push({
        ...p,
        // Unique synthetic ID so productMap keys don't collide
        _id: `${String(p._id || p.id)}_v${i}`,
        // Preserve originals for navigation
        _parentId: String(p._id || p.id || ''),
        _variantId: String(v._id || ''),
        _variantIdx: i,
        _activeColor: cardColor, // used by discount scan to scope to this card's colour
        // Override display fields
        title: variantName,
        name: variantName,
        image: firstImg,
        imageUrl: firstImg,
        images: variantImages.length ? variantImages : parentImages,
        price: variantPrice,
        localPrice: variantPrice,
        // Carry the parent's variants so the discount engine can find ALL discounted
        // sizes of THIS colour (was emptied previously which broke per-variant discounts)
        variants: Array.isArray(p.variants) ? p.variants : [],
        options: [],
      });
    }
  }
  return out;
}

// User-facing mall base URL (used to iframe the live mall so the preview is
// pixel-identical to what shoppers see). Override with REACT_APP_USER_APP_URL
// during dev if needed.
const USER_APP_URL = (process.env.REACT_APP_USER_APP_URL || "https://moondala.one").replace(/\/+$/, "");

/**
 * StandaloneMallPreview
 * For routes like /shop/mall/preview where there's no live editor state to mirror —
 * just iframe the actual user mall using the shop's own ID. Guarantees the shop
 * sees exactly what their visitors see (same theme, same prices, same discounts).
 */
function StandaloneMallPreview({ shopParam, hideEditControls }) {
    const navigate = useNavigate();
    const [shopId, setShopId] = useState(shopParam || "");
    const [shopName, setShopName] = useState("");
    const [themeId, setThemeId] = useState("");
    const [loadingShop, setLoadingShop] = useState(!shopParam);
    // Bumped on every save → forces iframe remount with the latest saved theme
    const [reloadKey, setReloadKey] = useState(() => Date.now());

    const fetchShop = React.useCallback(async () => {
        try {
            const res = await getMyShopMallPage();
            const sId = res?.shop?._id || res?.shop?.id || "";
            const tId = res?.mallPage?.themeId || res?.page?.themeId || "";
            const sName = res?.shop?.shopName || res?.shop?.name || "";
            setShopId(String(sId || ""));
            setShopName(String(sName || ""));
            setThemeId(String(tId || ""));
            // New timestamp → unique iframe URL → fresh load with the just-saved theme
            setReloadKey(Date.now());
        } catch (e) {
            if (e?.status === 401 || String(e?.message || "").includes("401")) {
                window.location.href = '/shop/login';
            }
        } finally {
            setLoadingShop(false);
        }
    }, []);

    useEffect(() => {
        if (shopParam) { setLoadingShop(false); return; }
        fetchShop();
    }, [shopParam, fetchShop]);

    // Listen for `mall-saved` from the editor → re-fetch + remount the iframe so
    // the preview ALWAYS reflects the latest saved theme/layout immediately.
    useEffect(() => {
        const onSaved = () => { fetchShop(); };
        window.addEventListener('mall-saved', onSaved);
        return () => window.removeEventListener('mall-saved', onSaved);
    }, [fetchShop]);

    if (loadingShop) {
        return (
            <div className="min-h-screen bg-black text-white flex flex-col items-center justify-center gap-4">
                <Loader2 className="animate-spin text-purple-500" size={32} />
                <p className="text-zinc-500">Loading Preview...</p>
            </div>
        );
    }
    if (!shopId) {
        return (
            <div className="min-h-screen bg-black text-white flex flex-col items-center justify-center gap-4 p-6">
                <p className="text-zinc-400">Save your mall first to preview it.</p>
            </div>
        );
    }

    // Cache-buster + theme param ensure the iframe always loads the just-saved theme
    const qs = new URLSearchParams();
    if (themeId) qs.set("theme", themeId);
    qs.set("_t", String(reloadKey));
    const iframeSrc = `${USER_APP_URL}/shop/${encodeURIComponent(shopId)}/mall?${qs.toString()}`;

    return (
        <div className="min-h-screen w-full bg-black flex flex-col">
            {!hideEditControls && (
                <div className="flex items-center justify-between gap-3 px-4 py-2 bg-zinc-900 border-b border-zinc-800 text-white">
                    <div className="text-xs">
                        <span className="opacity-60">Live preview of </span>
                        <strong>{shopName || "your mall"}</strong>
                        <span className="opacity-60"> — theme:</span>
                        <strong className="ml-1">{themeId || "(default)"}</strong>
                    </div>
                    <div className="flex items-center gap-2">
                        <button
                            onClick={() => fetchShop()}
                            className="text-xs font-semibold px-3 py-1.5 rounded-lg bg-zinc-800 hover:bg-zinc-700 text-white border border-zinc-700"
                            title="Re-fetch latest saved theme + reload preview"
                        >
                            Refresh
                        </button>
                        <button
                            onClick={() => navigate('/shop/mall/edit')}
                            className="text-xs font-semibold px-3 py-1.5 rounded-lg bg-violet-600 hover:bg-violet-500 text-white"
                        >
                            Edit Mall
                        </button>
                        <a
                            href={iframeSrc}
                            target="_blank" rel="noopener noreferrer"
                            className="text-xs font-semibold px-3 py-1.5 rounded-lg bg-zinc-800 hover:bg-zinc-700 text-white border border-zinc-700"
                        >
                            Open in new tab ↗
                        </a>
                    </div>
                </div>
            )}
            <iframe
                // key forces a fresh load every time the theme or save key changes
                key={`${themeId}-${reloadKey}`}
                title="Mall preview"
                src={iframeSrc}
                style={{ flex: 1, width: "100%", border: 0, background: "#000" }}
            />
        </div>
    );
}

export default function ShopMallPreview({ hideEditControls, previewData, isMobile }) {
    const [searchParamsTop] = useSearchParams();
    // Standalone preview routes (no live editor state) → iframe the actual user mall
    // for guaranteed pixel-parity. Editor-embedded preview (previewData passed) keeps
    // the component renderer below.
    if (!previewData) {
        const shopParam = searchParamsTop.get("shop") || searchParamsTop.get("shopId")
                       || searchParamsTop.get("sid") || searchParamsTop.get("id");
        return <StandaloneMallPreview shopParam={shopParam} hideEditControls={hideEditControls} />;
    }
    return <EditorEmbeddedPreview previewData={previewData} hideEditControls={hideEditControls} isMobile={isMobile} />;
}

function EditorEmbeddedPreview({ hideEditControls, previewData, isMobile }) {
  const [data, setData] = useState(null);
  const [shopInfo, setShopInfo] = useState(null); // Store extra shop details like name
  const [products, setProducts] = useState([]);
  const [activeCollection, setActiveCollection] = useState("");
  const [loading, setLoading] = useState(true);
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  // const location = useLocation();

  // Expose navigate for Image Gallery deep links (needed by mall section components)
  useEffect(() => {
    window.__shopMallNavigate = navigate;
    return () => { delete window.__shopMallNavigate; };
  }, [navigate]);

  // Integrated Search State
  const [searchQuery, setSearchQuery] = useState(searchParams.get("q") || "");

  // Shop Param
  const shopParam = searchParams.get("shop") || searchParams.get("shopId") || searchParams.get("sid") || searchParams.get("id");

  // Keep Sync with URL for search
  useEffect(() => {
      const q = searchParams.get("q") || "";
      setSearchQuery(q);
  }, [searchParams]);

  // const handleSearch = (val) => {
  //     setSearchQuery(val);
  //     if (!hideEditControls) {
  //         // Inside Editor, local state is enough
  //     } else {
  //         // In standalone preview
  //         if (!val) searchParams.delete("q");
  //         else searchParams.set("q", val);
  //         setSearchParams(searchParams, { replace: true });
  //     }
  // };

  // Force reset on component mount to prevent stale state
  // Use timestamp to force re-mount detection
  const [mountKey] = useState(() => Date.now());
  
  useEffect(() => {
    setData(null);
    setShopInfo(null);
    setProducts([]);
    setLoading(true);
  }, [mountKey]);

  // If previewData is passed directly (from Editor), use it. otherwise fetch.
  useEffect(() => {
     if (previewData) {
         const normalized = extractMallResponse({
             mallPage: {
                 themeId: previewData.themeId,
                 pageBg: previewData.pageBg,
                 sections: Array.isArray(previewData.sections) ? previewData.sections : [],
                 collections: Array.isArray(previewData.collections) ? previewData.collections : [],
                 customSidebar: Array.isArray(previewData.customSidebar) ? previewData.customSidebar : [],
                 mallLogoUrl: previewData.mallLogoUrl || "",
                 mallHeaderUrl: previewData.mallHeaderUrl || "",
             },
             shop: previewData.shopInfo || {},
             products: Array.isArray(previewData.products) ? previewData.products : [],
         });

         setData(normalized.mallPage);
         setShopInfo(normalized.shop);
         setProducts(flattenVariants(normalized.products));
         setLoading(false);
         return;
     }

     let cancelled = false;
     setLoading(true);

     async function load() {
         try {
             const response = shopParam
                 ? await getPublicShopMallPage(shopParam)
                 : await getMyShopMallPage();

             let allProducts = [];
             if (!shopParam) {
                 try {
                     const productsResponse = await getMyShopProducts();
                     allProducts = Array.isArray(productsResponse)
                         ? productsResponse
                         : (productsResponse?.items || productsResponse?.products || []);
                 } catch { /* fall back to mall products */ }
             }

             if (cancelled) return;

             const normalized = extractMallResponse(response);
             setData(normalized.mallPage);
             setShopInfo(normalized.shop);
             const finalProducts = allProducts.length > 0 ? allProducts : normalized.products;
             setProducts(flattenVariants(finalProducts));
         } catch (e) {
             if (!cancelled) {
                 if (e?.status === 401 || e?.message?.includes("401")) {
                     window.location.href = '/shop/login';
                 } else {
                     setData({ sections: [], themeId: 'midnight' });
                     setShopInfo({});
                     setProducts([]);
                 }
             }
         } finally {
             if (!cancelled) setLoading(false);
         }
     }

     load();
     return () => {
         cancelled = true;
     };
  }, [shopParam, previewData, mountKey]); // Include mountKey to force reload

  // Listen for save events from editor → reload preview
  useEffect(() => {
      const handleMallSaved = () => {
          if (!previewData) {
              setLoading(true);
              setData(null);
              setShopInfo(null);
              setProducts([]);
          }
      };
      window.addEventListener('mall-saved', handleMallSaved);
      return () => window.removeEventListener('mall-saved', handleMallSaved);
  }, [previewData]);

  const safeData = data || { sections: [], themeId: 'midnight' };

  // 1. Resolve Theme
  const themeId = safeData.themeId || 'midnight';
  const theme = THEMES[themeId] || THEMES.midnight;

  const productMap = useMemo(() => {
      const map = {};
      (Array.isArray(products) ? products : []).forEach((product) => {
          if (!product) return;
          const id = String(product._id || product.id || product.productId || "").trim();
          if (id && !map[id]) map[id] = product;
          const parentId = String(product._parentId || "").trim();
          if (parentId && !map[parentId]) map[parentId] = product;
          const variantId = String(product._variantId || "").trim();
          if (parentId && variantId) {
              const compositeKey = `${parentId}__${variantId}`;
              if (!map[compositeKey]) map[compositeKey] = product;
          }
      });
      return map;
  }, [products]);

  // Helper: Normalize strings for consistent comparison (Title Case)
  const normalize = (str) => {
      if (!str) return '';
      return String(str).trim()
          .split(' ')
          .map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
          .join(' ');
  };

  // Case/whitespace-insensitive key for matching industry/category links to products
  const matchKey = (str) => String(str || '').trim().toLowerCase().replace(/\s+/g, ' ');

  // Helper: Get query params for filtering
  // Note: We use the already memoized 'searchParams' from above if possible, but location.search dependency is fine here for derived state
  const filterInd = searchParams.get('industry'); 
  const filterCat = searchParams.get('category');

  /* ── Navigation helpers for ShopLayout ── */
  const handleGoHome = () => {
    setSearchQuery("");
    navigate({ search: "" });          // clear all query params → back to frontpage
  };
  const handleNavigate = (url) => {
    navigate({ search: url });          // e.g. "?industry=X&category=Y"
  };
  
  // Filter Logic (Search + Ind/Cat + Collection)
  const activeCustomCat = searchParams.get("customCat") || "";
  const activeCustomCatData = useMemo(() => {
    if (!activeCustomCat) return null;
    const [indId, catId] = activeCustomCat.split(":");
    for (const ind of (safeData.customSidebar || data?.customSidebar || [])) {
      if (ind.id === indId) {
        const cat = (ind.categories || []).find(c => c.id === catId);
        if (cat) return { ...cat, industryName: ind.industryName };
      }
    }
    return null;
  }, [activeCustomCat, safeData.customSidebar, data?.customSidebar]);

  // Build discount map: ONLY stores exact keys (productId or productId__variantId).
  // We never blindly index a bare parentId from a "pid__vid" entry — that would
  // incorrectly discount sibling colours/sizes (e.g. "blue/S" discounting brown).
  // Sources:
  //   1. customSidebar discount categories
  //   2. Per-variant discountPct set directly on each variant (from products page)
  // ONE source of truth: customSidebar sale categories only.
  const previewDiscountMap = useMemo(() => {
    const map = {};
    const set = (key, pct) => { if (key && (!map[key] || map[key] < pct)) map[key] = pct; };
    for (const ind of (safeData.customSidebar || data?.customSidebar || [])) {
      for (const cat of (ind.categories || [])) {
        const pct = Number(cat.discount) || 0;
        if (pct <= 0) continue;
        for (const id of (cat.productIds || [])) set(id, pct);
      }
    }
    return map;
  }, [safeData.customSidebar, data?.customSidebar]);

  // Apply discount to a product, scoping variant scan to THIS card's colour
  // so "brown/S only" never discounts the blue card.
  const discountedProducts = useMemo(() => {
    const applyDiscount = (p) => {
      const pid = String(p._parentId || p._id || p.id || "");
      const vid = String(p._variantId || p.variantId || "");

      // Whole product discounted
      if (previewDiscountMap[pid]) {
        const pct = previewDiscountMap[pid];
        return { ...p, _discountPct: pct,
          _originalPrice: p.price ?? p.localPrice,
          price:      Math.round((p.price      ?? p.localPrice ?? 0) * (1 - pct / 100) * 100) / 100,
          localPrice: Math.round((p.localPrice ?? p.price      ?? 0) * (1 - pct / 100) * 100) / 100 };
      }
      // Exact representative variant matches
      if (vid && previewDiscountMap[`${pid}__${vid}`]) {
        const pct = previewDiscountMap[`${pid}__${vid}`];
        return { ...p, _discountPct: pct,
          _originalPrice: p.price ?? p.localPrice,
          price:      Math.round((p.price      ?? p.localPrice ?? 0) * (1 - pct / 100) * 100) / 100,
          localPrice: Math.round((p.localPrice ?? p.price      ?? 0) * (1 - pct / 100) * 100) / 100 };
      }
      // Scan THIS card's variants (scoped to its own colour)
      const allVariants = Array.isArray(p.variants) ? p.variants : [];
      const activeColor = p._activeColor ? String(p._activeColor).toLowerCase() : "";
      const ownVariants = activeColor
        ? allVariants.filter((v) =>
            (v.optionValues || []).some((ov) => String(ov.value || "").toLowerCase() === activeColor))
        : allVariants;
      if (ownVariants.length > 0) {
        const variantDiscounts = ownVariants.map((v) => ({
          id: String(v._id || ""),
          pct: previewDiscountMap[`${pid}__${String(v._id || "")}`] || 0,
        })).filter((v) => v.id && v.pct > 0);
        if (variantDiscounts.length > 0) {
          const pct = Math.min(...variantDiscounts.map((v) => v.pct));
          const isPartial = variantDiscounts.length < ownVariants.length;
          if (isPartial) {
            // Only some sizes discounted — keep card price, show "from X% OFF" badge
            return { ...p, _discountPct: pct, _partialDiscount: true,
              _discountedVariantIds: variantDiscounts.map((v) => v.id) };
          }
          // All sizes of this colour discounted → discount the card price
          return { ...p, _discountPct: pct,
            _discountedVariantIds: variantDiscounts.map((v) => v.id),
            _originalPrice: p.price ?? p.localPrice,
            price:      Math.round((p.price      ?? p.localPrice ?? 0) * (1 - pct / 100) * 100) / 100,
            localPrice: Math.round((p.localPrice ?? p.price      ?? 0) * (1 - pct / 100) * 100) / 100 };
        }
      }
      return p;
    };
    return (Array.isArray(products) ? products : []).map(applyDiscount);
  }, [products, previewDiscountMap]);

  // Same as productMap but built from discountedProducts so picked-product sections
  // (FeaturedProduct, ProductGrid with manually-selected products) show the discount
  const discountedProductMap = useMemo(() => {
      const map = {};
      (Array.isArray(discountedProducts) ? discountedProducts : []).forEach((product) => {
          if (!product) return;
          const id = String(product._id || product.id || product.productId || "").trim();
          if (id && !map[id]) map[id] = product;
          const parentId = String(product._parentId || "").trim();
          if (parentId && !map[parentId]) map[parentId] = product;
          const variantId = String(product._variantId || "").trim();
          if (parentId && variantId) {
              const compositeKey = `${parentId}__${variantId}`;
              if (!map[compositeKey]) map[compositeKey] = product;
          }
      });
      return map;
  }, [discountedProducts]);

  // parent id → all colour cards (mirrors the user mall's parentToCards)
  const discountedParentToCards = useMemo(() => {
      const byParent = {};
      (Array.isArray(discountedProducts) ? discountedProducts : []).forEach((product) => {
          if (!product) return;
          const pid = String(product._parentId || product._id || product.id || "").trim();
          if (!pid) return;
          if (!byParent[pid]) byParent[pid] = [];
          byParent[pid].push(product);
      });
      return byParent;
  }, [discountedProducts]);

  const matchesIdSetPreview = (p, ids) => {
    const pid = String(p._parentId || p._id || p.id || "");
    const vid = String(p._variantId || p.variantId || "");
    const variantKey = vid ? `${pid}__${vid}` : null;
    if (ids.has(pid) || (variantKey && ids.has(variantKey)) || (vid && ids.has(vid))) return true;
    return (Array.isArray(p.variants) ? p.variants : []).some(v => {
      const vId = String(v._id || "");
      return vId && ids.has(`${pid}__${vId}`);
    });
  };

  const filteredProducts = useMemo(() => {
      const showAll = searchParams.get("showAll") === "true";
      if (showAll) return discountedProducts;

      // Custom sidebar category filter — discount already applied via discountedProducts
      if (activeCustomCat && activeCustomCatData) {
          // Show the discount that belongs to THIS category, not the global max.
          // (matches user mall behaviour — e.g. card in "30% sale" shows 30% even if
          // some sibling variant happens to be in a "50% sale" category)
          const ids = new Set((activeCustomCatData.productIds || []).map(String));
          const catPct = Math.min(100, Math.max(0, Number(activeCustomCatData.discount) || 0));

          return discountedProducts.filter(p => matchesIdSetPreview(p, ids)).map(p => {
              if (catPct <= 0) return p;
              const pid = String(p._parentId || p._id || p.id || "");
              const catVariantIds = [];
              for (const cid of ids) {
                  const parts = String(cid).split("__");
                  if (parts[0] === pid && parts[1]) catVariantIds.push(parts[1]);
              }
              const activeColor = p._activeColor ? String(p._activeColor).toLowerCase() : "";
              const ownVariants = activeColor
                  ? (Array.isArray(p.variants) ? p.variants : []).filter(v =>
                      (v.optionValues || []).some(ov => String(ov.value || "").toLowerCase() === activeColor))
                  : (Array.isArray(p.variants) ? p.variants : []);
              const ownDiscountedIds = ownVariants
                  .map(v => String(v._id || ""))
                  .filter(vid => vid && catVariantIds.includes(vid));
              const wholeProductInCategory = catVariantIds.length === 0 && ids.has(pid);
              const isPartial = ownDiscountedIds.length > 0 && ownDiscountedIds.length < ownVariants.length;
              const originalPrice = p._originalPrice ?? p.price ?? p.localPrice ?? 0;
              if (isPartial && !wholeProductInCategory) {
                  return {
                      ...p, _discountPct: catPct, _partialDiscount: true,
                      _discountedVariantIds: ownDiscountedIds,
                      _originalPrice: undefined,
                      price: originalPrice, localPrice: originalPrice,
                  };
              }
              return {
                  ...p, _discountPct: catPct, _partialDiscount: false,
                  _discountedVariantIds: ownDiscountedIds.length > 0 ? ownDiscountedIds : null,
                  _originalPrice: originalPrice,
                  price:      Math.round(originalPrice * (1 - catPct / 100) * 100) / 100,
                  localPrice: Math.round(originalPrice * (1 - catPct / 100) * 100) / 100,
              };
          });
      }

      // 0. Collection filter (overrides ind/cat)
      if (activeCollection) {
          const col = (data?.collections || []).find(c => c.id === activeCollection);
          const ids = new Set((col?.productIds || []).map(String));
          return discountedProducts.filter(p => ids.has(String(p._id || p.id || "")));
      }

      return discountedProducts.filter(p => {
          // 1. Search Logic
          if (searchQuery) {
              const q = searchQuery.toLowerCase();
              const title = String(p.title || p.name || "").toLowerCase();
              if (!title.includes(q)) return false;
          }

          // 2. Industry Logic
          if (filterInd) {
              const pInd = normalize(p.industry || p.industryLabel || p.industryName || 'Other Industries');
              if (matchKey(pInd) !== matchKey(filterInd)) return false;
          }

          // 3. Category Logic
          if (filterCat) {
              const pCat = normalize(p.category || p.categoryLabel || p.categoryName || 'Other');
              if (matchKey(pCat) !== matchKey(filterCat)) return false;
          }
          return true;
      });
    }, [discountedProducts, filterInd, filterCat, searchQuery, activeCollection, data?.collections, activeCustomCat, activeCustomCatData, searchParams]);

  // ✅ SHOW ALL PRODUCTS when explicitly viewing all (no filters)
  // When user clicks "Show All Products" button, we want to display the complete inventory
  const showAllProductsView = !!(searchParams.get('showAll') === 'true');
  
  const fallbackProducts = useMemo(() => {
      // If filtering OR explicitly showing all, show complete list
      const isFiltering = !!(filterInd || filterCat || searchQuery);
      return (isFiltering || showAllProductsView) ? filteredProducts : filteredProducts.slice(0, 8);
  }, [filteredProducts, filterInd, filterCat, searchQuery, showAllProductsView]);


  const industryLinks = useMemo(() => {
      const hierarchy = {};
      const categoryProducts = {}; // Track products per category to get images
      
      (Array.isArray(products) ? products : []).forEach(p => {
          const rawInd = p.industry || p.industryLabel || p.industryName || "Other Industries";
          const rawCat = p.category || p.categoryLabel || p.categoryName || "Other";
          
          const ind = normalize(rawInd);
          const cat = normalize(rawCat);
          const key = `${ind}::${cat}`;

          if (!hierarchy[ind]) hierarchy[ind] = new Set();
          hierarchy[ind].add(cat);
          
          // Store first product image for this category (check all image sources)
          if (!categoryProducts[key]) {
              const img =
                  p.image ||
                  p.imageUrl ||
                  p.thumbnail ||
                  (Array.isArray(p.images) && p.images.filter(Boolean)[0]) ||
                  p?.media?.image ||
                  (Array.isArray(p?.media?.images) && p.media.images.filter(Boolean)[0]) ||
                  null;
              if (img) categoryProducts[key] = img;
          }
      });
      
      return Object.entries(hierarchy).sort().map(([ind, cats]) => ({
          label: ind,
          children: Array.from(cats).sort().map(c => ({
              label: c,
              url: `?industry=${encodeURIComponent(ind)}&category=${encodeURIComponent(c)}`,
              image: categoryProducts[`${ind}::${c}`] || null // Add representative image
          }))
      }));
  }, [products]);

  // Build custom sidebar nav links from customSidebar data
  const customSidebarLinks = useMemo(() => {
    const sidebar = safeData.customSidebar || data?.customSidebar || [];
    return sidebar.map(industry => ({
      label: industry.industryName,
      isCustom: true,
      children: (industry.categories || []).map(cat => ({
        label: cat.name,
        discount: cat.discount || 0,
        productIds: cat.productIds || [],
        url: `?customCat=${encodeURIComponent(industry.id + ':' + cat.id)}`,
      })),
    }));
  }, [safeData.customSidebar, data?.customSidebar]);

  // 2. Resolve Sections (handle empty or legacy)
  const rawSections = Array.isArray(safeData.sections) ? safeData.sections : [];

  // Navigation Menu "manual" mode → the shop's own links replace the auto category links
  // (same rule as the user mall page)
  const navSectionForLinks = rawSections.find(s => String(s.type || "").toLowerCase() === 'navigationmenu');
  const navPropsForLinks = resolveProps(navSectionForLinks);
  const manualNavLinks = (navPropsForLinks.mode === 'manual' && Array.isArray(navPropsForLinks.links))
    ? navPropsForLinks.links
        .filter(l => l && String(l.label || '').trim())
        .map(l => ({ label: String(l.label).trim(), url: l.url && l.url !== '#' ? l.url : '' }))
    : null;
  const manualNavKey = JSON.stringify(manualNavLinks);
  const finalNavLinks = useMemo(
    () => (manualNavLinks ? [...manualNavLinks, ...customSidebarLinks] : [...industryLinks, ...customSidebarLinks]),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [industryLinks, customSidebarLinks, manualNavKey]
  );

  // Filter out Logic sections (NavigationMenu) from the visual stack
  // They are handled via the ShopLayout props
  const visualSections = rawSections.filter(s => {
      const t = String(s.type || "").toLowerCase();
      return t !== 'navigationmenu'; // Case-insensitive check
  });
  
  // Extract Navigation Props
  // const navSection = rawSections.find(s => {
  //    const t = String(s.type || "").toLowerCase();
  //    return t === 'navigationmenu';
  // });
  // const navProps = resolveProps(navSection);
  // const navLinks = navProps.links || [];

  // 3. Fallback for Empty State (New User)
  const showStarter = visualSections.length === 0 && !hideEditControls;
  
  // Resolve Shop Name: 1. Shop Info (DB) -> 2. Page Data (Editor) -> 3. Fallback
  const finalShopName = shopInfo?.name || safeData.shopName || "My Store";
  


  if (loading) {
      return (
          <div className="min-h-screen bg-black text-white flex flex-col items-center justify-center gap-4">
              <Loader2 className="animate-spin text-purple-500" size={32} />
              <p className="text-zinc-500">Loading Store...</p>
          </div>
      );
  }

  /* 
     === FILTERED VIEW (Category OR Search OR Show All) === 
     If the user searches or clicks a category OR clicks "Show All", replace the home layout with a grid results page.
  */
  const hasActiveFilters = !!(filterInd || filterCat || searchQuery || showAllProductsView || activeCustomCat);

  if (hasActiveFilters) {
      const profileSection = visualSections.find(s => s.type === 'ProfileHeader');
      const rawProfileProps = profileSection ? resolveProps(profileSection) : { shopName: finalShopName, bio: shopInfo?.bio };
      /* Auto-inject shop profile avatar & cover */
      const profileProps = {
        ...rawProfileProps,
        avatarUrl: rawProfileProps.avatarUrl || shopInfo?.logoUrl || shopInfo?.logo || "",
        coverUrl: rawProfileProps.coverUrl || shopInfo?.coverImage || "",
        shopName: rawProfileProps.shopName || shopInfo?.shopName || shopInfo?.name || "",
        bio: rawProfileProps.bio || shopInfo?.bio || "",
      };
      
      // Dynamic Title
      let resultsTitle = "";
      if (searchQuery) resultsTitle = `Search results for "${searchQuery}"`;
      else if (activeCustomCat && activeCustomCatData) {
        resultsTitle = `${activeCustomCatData.industryName} › ${activeCustomCatData.name}`;
      }
      else if (showAllProductsView) resultsTitle = "All Products";
      else resultsTitle = `${filterInd}${filterCat ? ` › ${filterCat}` : ''}`;
      
      return (
        <ShopLayout 
            theme={theme} 
            shopName={finalShopName} 
            isMobile={isMobile}
            navLinks={finalNavLinks}
            collections={[]}
            activeCollection={activeCollection}
            onCollection={(id) => { setActiveCollection(id); handleGoHome(); }}
            exitUrl={shopParam ? "/" : "/shop/dashboard"}
            searchQuery={searchQuery}
            onSearch={setSearchQuery}
            onHome={handleGoHome}
            onNavigate={handleNavigate}
        >
             <div className="w-full min-h-[80vh] flex flex-col items-center">
                <div className="w-full max-w-6xl">
                    
                    {/* Header (Preserve Identity) */}
                    {profileSection && (
                         <div className="relative group">
                            <COMPONENT_MAP.ProfileHeader data={profileProps} theme={theme} />
                         </div>
                    )}

                    {/* Filtered Grid */}
                    <div className="px-6 py-8 animate-in fade-in slide-in-from-bottom-4 duration-500">
                        {/* Discount banner for custom sidebar categories */}
                        {activeCustomCatData?.discount > 0 && (
                            <div className="mb-6 px-5 py-3 rounded-2xl flex items-center gap-3 font-semibold text-sm"
                                style={{ background: `linear-gradient(135deg, #ef4444, #f97316)`, color: "#fff" }}>
                                <span style={{ fontSize: 20 }}>🔥</span>
                                <span>{activeCustomCatData.discount}% OFF automatically applied at checkout on all items in <strong>{activeCustomCatData.name}</strong></span>
                            </div>
                        )}
                        <h2 className="text-2xl font-bold mb-8 flex items-center gap-3" style={{ color: theme.text }}>
                            <span className="opacity-50 font-normal">
                                {searchQuery ? "Searching:" : "Browsing:"}
                            </span> 
                            {resultsTitle}
                        </h2>
                        
                        {filteredProducts.length > 0 ? (
                            showAllProductsView ? (
                                // ✅ Show All: Group by Industry > Category
                                <div className="space-y-12">
                                    {Object.entries(industryLinks).map(([index, industry]) => {
                                        const industryName = industry.label;
                                        const categories = industry.children || [];
                                        
                                        return (
                                            <div key={index} className="space-y-8">
                                                {/* Industry Header */}
                                                <div className="border-b-2 pb-3" style={{ borderColor: theme.primary }}>
                                                    <h3 className="text-2xl font-bold" style={{ color: theme.primary }}>
                                                        {industryName}
                                                    </h3>
                                                </div>
                                                
                                                {/* Categories within Industry */}
                                                {categories.map((category, catIndex) => {
                                                    const categoryName = category.label;
                                                    const categoryProducts = filteredProducts.filter(p => {
                                                        const pInd = normalize(p.industry || p.industryLabel || p.industryName || 'Other Industries');
                                                        const pCat = normalize(p.category || p.categoryLabel || p.categoryName || 'Other');
                                                        return pInd === industryName && pCat === categoryName;
                                                    });
                                                    
                                                    if (categoryProducts.length === 0) return null;
                                                    
                                                    return (
                                                        <div key={catIndex} className="space-y-4">
                                                            {/* Category Header - Clickable */}
                                                            <button 
                                                                onClick={() => {
                                                                    const url = `?industry=${encodeURIComponent(industryName)}&category=${encodeURIComponent(categoryName)}`;
                                                                    navigate({ search: url });
                                                                }}
                                                                className="w-full text-left text-lg font-semibold opacity-90 hover:opacity-100 transition-all cursor-pointer group flex items-center gap-2 px-4 py-2 rounded-lg hover:bg-white/5 border border-transparent hover:border-white/10"
                                                                style={{ color: theme.text }}
                                                            >
                                                                <span className="group-hover:underline decoration-2 underline-offset-4">{categoryName}</span>
                                                                <span className="text-xs opacity-50 group-hover:opacity-100 font-bold px-2 py-0.5 rounded-full" style={{ backgroundColor: theme.primary + '20' }}>
                                                                    {categoryProducts.length}
                                                                </span>
                                                                <svg className="w-4 h-4 opacity-0 group-hover:opacity-100 transition-all group-hover:translate-x-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                                                                </svg>
                                                                <span className="ml-auto text-xs opacity-0 group-hover:opacity-60 transition-opacity">Click to filter</span>
                                                            </button>
                                                            
                                                            {/* Products in this Category */}
                                                            <COMPONENT_MAP.ProductGrid 
                                                                data={{ products: categoryProducts, title: "" }} 
                                                                theme={theme} 
                                                            />
                                                        </div>
                                                    );
                                                })}
                                            </div>
                                        );
                                    })}
                                </div>
                            ) : (
                                // Regular filtered view - single grid
                                <COMPONENT_MAP.ProductGrid 
                                    data={{ products: filteredProducts, title: "" }} 
                                    theme={theme} 
                                />
                            )
                        ) : (
                            <div className="flex flex-col items-center justify-center py-24 text-center border-2 border-dashed rounded-3xl" style={{ borderColor: theme.border }}>
                                <div className="text-6xl mb-4 opacity-20">📦</div>
                                <h3 className="text-xl font-bold mb-2 opacity-50" style={{ color: theme.text }}>No products found</h3>
                                <p className="text-sm opacity-40 max-w-xs mx-auto" style={{ color: theme.text }}>
                                    We couldn't find any items matching your criteria.
                                </p>
                                {searchQuery && (
                                    <button 
                                        onClick={() => setSearchQuery("")}
                                        className="mt-4 text-sm underline opacity-60 hover:opacity-100"
                                        style={{ color: theme.primary }}
                                    >
                                        Clear Search
                                    </button>
                                )}
                            </div>
                        )}
                    </div>
                </div>
             </div>
        </ShopLayout>
      );
  }

  /* 
     === STANDARD HOME VIEW === 
     Show full flexible layout defined by the Editor.
  */
  // Hide old collections when shop has built customSidebar (avoids duplicates)
  const hasCustomSidebar = (safeData.customSidebar || data?.customSidebar || []).length > 0;
  const visibleCollections = hasCustomSidebar ? [] : (data?.collections || []);

  return (
    /* Wrap entire page in ShopLayout (handles Top/Side Nav) */
    <ShopLayout
        theme={theme}
        shopName={finalShopName}
        isMobile={isMobile}
        navLinks={finalNavLinks}
        collections={visibleCollections}
        activeCollection={activeCollection}
        onCollection={(id) => { setActiveCollection(id); handleGoHome(); }}
        exitUrl={shopParam ? "/" : "/shop/dashboard"}
        searchQuery={searchQuery}
        onSearch={setSearchQuery}
        onHome={handleGoHome}
        onNavigate={handleNavigate}
    >
        <div className="w-full min-h-[80vh] flex flex-col items-center">
            
            {/* Sections Container - Removed padding-y so headers touch the top */}
            <div className="w-full max-w-4xl">
                {visualSections && visualSections.length > 0 ? (
                    visualSections.map((section, index) => {
                        const typeKey = String(section.type || "custom_block").trim();
                        const Component = COMPONENT_MAP[typeKey] || COMPONENT_MAP[typeKey.toLowerCase()];

                        if (!Component) {
                            return (
                                <div key={index} className="p-4 border border-red-500/50 rounded text-red-500 text-xs">
                                    Unknown Component: {typeKey}
                                </div>
                            );
                        }

                        try {
                            const resolvedProps = resolveProps(section);
                            const typeLower = typeKey.toLowerCase();
                            let componentData = resolvedProps;

                            /* Auto-inject shop profile avatar & cover for ProfileHeader
                               Mall-specific logo/header (resolvedProps) takes priority over shop account images */
                            if (typeLower === 'profileheader' || typeLower === 'profile_header') {
                                componentData = {
                                    ...resolvedProps,
                                    avatarUrl: resolvedProps.avatarUrl || shopInfo?.logoUrl || shopInfo?.logo || "",
                                    coverUrl: resolvedProps.coverUrl || shopInfo?.coverImage || "",
                                    shopName: resolvedProps.shopName || shopInfo?.shopName || shopInfo?.name || "",
                                    bio: resolvedProps.bio || shopInfo?.bio || "",
                                };
                            }

                            if (typeLower === 'productgrid' || typeLower === 'featuredproduct' || typeLower === 'featured_product') {
                                const rawIds = Array.isArray(resolvedProps.productIds) ? resolvedProps.productIds : [];
                                const ids = rawIds.map((id) => String(id || "").trim()).filter(Boolean);
                                // Same as the user mall: a picked parent id expands to one card per colour;
                                // a "parentId__variantId" pick is that exact card. Discounted prices included.
                                const selectedProducts = ids.flatMap((id) => {
                                    const cards = discountedParentToCards[id];
                                    if (cards && cards.length > 0) return cards;
                                    const card = discountedProductMap[id];
                                    return card ? [card] : [];
                                });

                                // Grid with NO picks → newest products. Picks that are gone just drop out;
                                // Featured Product never falls back (hidden on the user mall if its product is gone).
                                const productsForGrid = (typeLower === 'productgrid' && ids.length === 0) ? fallbackProducts : selectedProducts;

                                componentData = {
                                    ...resolvedProps,
                                    productIds: ids,
                                    products: productsForGrid,
                                };
                            }

                            // Auto-resolve product images for ImageGallery sections
                            // If an image has productId but no url, look up the product and fill in the image
                            if (typeLower === 'imagegallery' || typeLower === 'image_gallery') {
                                const rawImages = Array.isArray(resolvedProps.images) ? resolvedProps.images : [];
                                const resolvedImages = rawImages.map((img) => {
                                    if (typeof img === "string" && img) return img;
                                    if (!img || typeof img !== "object") return img;
                                    if (img.url) return img; // already has a URL, no action needed
                                    const rawProductId = img.productId || "";
                                    if (!rawProductId) return img;
                                    // Support both "parentId__variantId" and plain "parentId"
                                    const parentId = rawProductId.split("__")[0];
                                    const product = productMap[parentId] || productMap[rawProductId];
                                    if (!product) return img;
                                    // Pick best image from product
                                    const productImg =
                                        product.image ||
                                        product.imageUrl ||
                                        product.thumbnail ||
                                        product.photo ||
                                        (Array.isArray(product.images) && product.images.find(Boolean)) ||
                                        product?.media?.image ||
                                        (Array.isArray(product?.media?.images) && product.media.images.find(Boolean)) ||
                                        "";
                                    return productImg ? { ...img, url: productImg } : img;
                                });
                                componentData = { ...resolvedProps, images: resolvedImages };
                            }

                            return (
                                <div key={section.id || index} className="relative group">
                                    <Component data={componentData} theme={theme} shopData={shopInfo} />
                                </div>
                            );
                        } catch (error) {
                            return (
                                <div key={index} className="p-4 border border-red-500/50 rounded text-red-500 text-xs">
                                    Error rendering {typeKey}: {error.message}
                                </div>
                            );
                        }
                    })
                ) : (
                    <div className="text-center py-12 text-zinc-400">
                        <p>No sections configured yet.</p>
                        {!hideEditControls && (
                            <p className="text-sm mt-2">Go to the editor to add sections.</p>
                        )}
                    </div>
                )}

                {/* Empty State / Welcome Placeholder */}
                {showStarter && (
                    <div className="flex flex-col items-center justify-center h-64 text-center opacity-50 border-2 border-dashed rounded-3xl m-8" style={{ borderColor: theme.border }}>
                        <p style={{ color: theme.text }}>Your shop page is empty.</p>
                        <p className="text-sm mt-2" style={{ color: theme.text }}>Add sections in the editor to get started.</p>
                    </div>
                )}
                
                {/* Footer / Branding */}
                <div className="text-center py-12 opacity-30 text-xs" style={{ color: theme.text }}>
                    Powered by Moondala
                </div>
            </div>
        </div>
    </ShopLayout>
  );
}
